import { type IBrand } from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  type DocumentReference,
  Firestore,
  type IDataTable,
  undeletedFilter,
  where,
} from '@principle-theorem/shared';
import { Brand } from '../models/brand';

interface IContactSummaryRecord {
  contactName: string;
  phoneNumber: string;
  email: string;
  address: string;
  patientsReferred: number;
}

interface IContactReferralDetailRecord {
  contactName: string;
  patientName: string;
}

export class ContactReferralSummary {
  async run(
    brandRef: DocumentReference<IBrand>
  ): Promise<
    IDataTable<IContactSummaryRecord | IContactReferralDetailRecord>[]
  > {
    const contacts = await Firestore.getDocs(
      Brand.contactCol(asReffable(brandRef))
    );
    const undeletedContacts = contacts.filter(undeletedFilter);
    const brand = await Firestore.getDoc(brandRef);

    const summaryRecords: IContactSummaryRecord[] = [];
    const detailRecords: IContactReferralDetailRecord[] = [];

    for (const contact of undeletedContacts) {
      const referrals = await Firestore.getDocs(
        Brand.patientCol(asReffable(brandRef)),
        where('referrer.ref', '==', contact.ref)
      );

      summaryRecords.push({
        contactName: contact.name,
        phoneNumber: contact.phone ?? '',
        email: contact.email ?? '',
        address: contact.address ?? '',
        patientsReferred: referrals.length,
      });

      for (const referral of referrals) {
        detailRecords.push({
          contactName: contact.name,
          patientName: referral.name,
        });
      }
    }

    return [
      {
        name: `${brand.name} Contact Summary`,
        data: summaryRecords,
        columns: [
          { key: 'contactName', header: 'Contact Name' },
          { key: 'phoneNumber', header: 'Phone Number' },
          { key: 'email', header: 'Email' },
          { key: 'address', header: 'Address' },
          { key: 'patientsReferred', header: 'Patients Referred' },
        ],
      },
      {
        name: `${brand.name} Referral Details`,
        data: detailRecords,
        columns: [
          { key: 'contactName', header: 'Contact Name' },
          { key: 'patientName', header: 'Patient Name' },
        ],
      },
    ];
  }
}
